import chalk from 'chalk';
import ora from 'ora';
import type { CacheOperation } from './types';

/**
 * 缓存性能监控和指标收集器
 * 提供详细的性能指标、监控和报告功能
 */
export class CacheMetricsCollector {
  private metrics: Map<string, any> = new Map();
  private operations: CacheOperation[] = [];
  private spinner = ora();
  private maxOperationsHistory = 10000;

  constructor() {
    this.initializeMetrics();
  }

  /**
   * 记录缓存操作
   */
  recordOperation(operation: CacheOperation): void {
    this.operations.push(operation);

    // 限制操作历史大小
    if (this.operations.length > this.maxOperationsHistory) {
      this.operations = this.operations.slice(-this.maxOperationsHistory);
    }

    // 更新实时指标
    this.updateRealTimeMetrics(operation);
  }

  /**
   * 获取性能指标
   */
  getMetrics(): {
    realTime: any;
    historical: any;
    performance: any;
    recommendations: string[];
  } {
    return {
      realTime: this.getRealTimeMetrics(),
      historical: this.getHistoricalMetrics(),
      performance: this.getPerformanceMetrics(),
      recommendations: this.generateRecommendations(),
    };
  }

  /**
   * 生成性能报告
   */
  async generateReport(): Promise<void> {
    this.spinner.start('生成缓存性能报告...');

    try {
      const metrics = this.getMetrics();

      console.log(chalk.blue('\n📊 缓存性能报告'));
      console.log(chalk.gray('='.repeat(50)));

      // 实时指标
      console.log(chalk.yellow('\n🔄 实时指标:'));
      console.log(chalk.gray(`总操作数: ${metrics.realTime.totalOperations}`));
      console.log(
        chalk.gray(
          `成功率: ${(metrics.realTime.successRate * 100).toFixed(2)}%`
        )
      );
      console.log(
        chalk.gray(
          `平均响应时间: ${metrics.realTime.avgResponseTime.toFixed(2)}ms`
        )
      );
      console.log(chalk.gray(`QPS: ${metrics.realTime.qps.toFixed(2)}`));

      // 历史指标
      console.log(chalk.yellow('\n📈 历史指标:'));
      console.log(chalk.gray(`操作分布:`));
      Object.entries(metrics.historical.operationDistribution).forEach(
        ([type, count]) => {
          console.log(chalk.gray(`  ${type}: ${count} 次`));
        }
      );

      // 性能指标
      console.log(chalk.yellow('\n⚡ 性能指标:'));
      console.log(
        chalk.gray(
          `P50 响应时间: ${metrics.performance.p50ResponseTime.toFixed(2)}ms`
        )
      );
      console.log(
        chalk.gray(
          `P95 响应时间: ${metrics.performance.p95ResponseTime.toFixed(2)}ms`
        )
      );
      console.log(
        chalk.gray(
          `P99 响应时间: ${metrics.performance.p99ResponseTime.toFixed(2)}ms`
        )
      );
      console.log(
        chalk.gray(
          `错误率: ${(metrics.performance.errorRate * 100).toFixed(2)}%`
        )
      );

      // 建议
      if (metrics.recommendations.length > 0) {
        console.log(chalk.yellow('\n💡 优化建议:'));
        metrics.recommendations.forEach((recommendation, index) => {
          console.log(chalk.gray(`${index + 1}. ${recommendation}`));
        });
      }

      this.spinner.succeed('性能报告生成完成');
    } catch (error) {
      this.spinner.fail('性能报告生成失败');
      console.error(chalk.red('报告错误:'), error);
    }
  }

  /**
   * 导出指标数据
   */
  exportMetrics(): Buffer {
    const metricsData = {
      metrics: this.getMetrics(),
      operations: this.operations.slice(-1000), // 导出最近1000个操作
      timestamp: Date.now(),
      version: '1.0.0',
    };

    return Buffer.from(JSON.stringify(metricsData, null, 2));
  }

  /**
   * 导入指标数据
   */
  importMetrics(data: Buffer): void {
    try {
      const metricsData = JSON.parse(data.toString());

      if (metricsData.operations) {
        this.operations = metricsData.operations;
      }

      if (metricsData.metrics) {
        this.metrics = new Map(Object.entries(metricsData.metrics));
      }

      console.log(chalk.green('指标数据导入成功'));
    } catch (error) {
      console.error(chalk.red('指标数据导入失败:'), error);
      throw error;
    }
  }

  /**
   * 重置指标
   */
  resetMetrics(): void {
    this.operations = [];
    this.initializeMetrics();
    console.log(chalk.green('指标已重置'));
  }

  /**
   * 获取实时指标
   */
  private getRealTimeMetrics(): any {
    const now = Date.now();
    const lastMinute = this.operations.filter(op => now - op.timestamp < 60000);

    const totalOperations = this.operations.length;
    const successfulOperations = this.operations.filter(
      op => op.success
    ).length;
    const successRate =
      totalOperations > 0 ? successfulOperations / totalOperations : 0;

    const avgResponseTime =
      totalOperations > 0
        ? this.operations.reduce((sum, op) => sum + op.duration, 0) /
          totalOperations
        : 0;

    const qps = lastMinute.length / 60; // 每分钟的操作数

    return {
      totalOperations,
      successfulOperations,
      successRate,
      avgResponseTime,
      qps,
      lastMinuteOperations: lastMinute.length,
    };
  }

  /**
   * 获取历史指标
   */
  private getHistoricalMetrics(): any {
    const operationDistribution: Record<string, number> = {};

    this.operations.forEach(op => {
      operationDistribution[op.type] =
        (operationDistribution[op.type] || 0) + 1;
    });

    const hourlyDistribution: Record<number, number> = {};
    this.operations.forEach(op => {
      const hour = new Date(op.timestamp).getHours();
      hourlyDistribution[hour] = (hourlyDistribution[hour] || 0) + 1;
    });

    return {
      operationDistribution,
      hourlyDistribution,
      totalOperations: this.operations.length,
      timeRange: {
        start: this.operations.length > 0 ? this.operations[0].timestamp : 0,
        end:
          this.operations.length > 0
            ? this.operations[this.operations.length - 1].timestamp
            : 0,
      },
    };
  }

  /**
   * 获取性能指标
   */
  private getPerformanceMetrics(): any {
    const durations = this.operations
      .map(op => op.duration)
      .sort((a, b) => a - b);
    const errors = this.operations.filter(op => !op.success);

    const p50Index = Math.floor(durations.length * 0.5);
    const p95Index = Math.floor(durations.length * 0.95);
    const p99Index = Math.floor(durations.length * 0.99);

    return {
      p50ResponseTime: durations[p50Index] || 0,
      p95ResponseTime: durations[p95Index] || 0,
      p99ResponseTime: durations[p99Index] || 0,
      errorRate:
        this.operations.length > 0 ? errors.length / this.operations.length : 0,
      errorCount: errors.length,
      maxResponseTime:
        durations.length > 0 ? durations[durations.length - 1] : 0,
      minResponseTime: durations.length > 0 ? durations[0] : 0,
    };
  }

  /**
   * 生成优化建议
   */
  private generateRecommendations(): string[] {
    const recommendations: string[] = [];
    const realTimeMetrics = this.getRealTimeMetrics();
    const performanceMetrics = this.getPerformanceMetrics();
    const historicalMetrics = this.getHistoricalMetrics();

    // 基于响应时间的建议
    if (performanceMetrics.p95ResponseTime > 1000) {
      recommendations.push('P95响应时间过长，建议优化缓存存储策略');
    }

    // 基于错误率的建议
    if (performanceMetrics.errorRate > 0.05) {
      recommendations.push('错误率较高，建议检查缓存配置和网络连接');
    }

    // 基于成功率的建议
    if (realTimeMetrics.successRate < 0.9) {
      recommendations.push('成功率较低，建议增加缓存预热和故障转移机制');
    }

    // 基于QPS的建议
    if (realTimeMetrics.qps > 100) {
      recommendations.push('QPS较高，建议考虑缓存分片和负载均衡');
    }

    // 基于操作分布的建议
    const operationDistribution = historicalMetrics.operationDistribution;
    if (
      operationDistribution.get &&
      operationDistribution.get > operationDistribution.set * 3
    ) {
      recommendations.push('读取操作远多于写入操作，建议优化缓存命中率');
    }

    return recommendations;
  }

  /**
   * 更新实时指标
   */
  private updateRealTimeMetrics(operation: CacheOperation): void {
    const key = `operation_${operation.type}`;
    const current = this.metrics.get(key) || {
      count: 0,
      totalDuration: 0,
      errors: 0,
    };

    current.count++;
    current.totalDuration += operation.duration;
    if (!operation.success) {
      current.errors++;
    }

    this.metrics.set(key, current);
  }

  /**
   * 初始化指标
   */
  private initializeMetrics(): void {
    this.metrics.set('total_operations', 0);
    this.metrics.set('total_duration', 0);
    this.metrics.set('total_errors', 0);
    this.metrics.set('start_time', Date.now());
  }

  /**
   * 获取指标摘要
   */
  getMetricsSummary(): {
    totalOperations: number;
    successRate: number;
    avgResponseTime: number;
    errorRate: number;
    uptime: number;
  } {
    const realTime = this.getRealTimeMetrics();
    const performance = this.getPerformanceMetrics();
    const startTime = this.metrics.get('start_time') || Date.now();

    return {
      totalOperations: realTime.totalOperations,
      successRate: realTime.successRate,
      avgResponseTime: realTime.avgResponseTime,
      errorRate: performance.errorRate,
      uptime: Date.now() - startTime,
    };
  }
}
